﻿<template>
  <main
    class="home-page"
    :class="{ 'home-page--side-menu-expanded': isSideMenuExpanded }"
    aria-label="Create mail-outs"
  >
    <div class="page-wrapper">
      <section class="hero-section" aria-label="Hero">
        <div v-if="isLoggedIn && isBusinessUser" class="home-top-nav">
          <pn-icon :icon="arrowLeftIcon" color="blue700"></pn-icon>
          <pn-text-link
            :label="$t('home.backToPostalServices')"
            :href="constants.HOME.POSTAL_SERVICE_URL"
          ></pn-text-link>
        </div>

        <!-- banner -->
        <section class="banner-card" aria-labelledby="banner-heading">
          <div class="banner-content">
            <div class="banner-text">
              <h1 id="banner-heading" class="banner-title pn-2xl">{{ $t('home.title') }}</h1>
              <p class="banner-description pn-m">{{ $t('home.titleDescription') }}</p>
            </div>
            <pn-button
              :key="locale"
              class="banner-cta"
              appearance="light"
              :icon="arrowRightIcon"
              @click="goToOrder"
            >
              {{ $t('home.titleLinkText') }}
            </pn-button>
          </div>
          <div class="banner-mockup" aria-hidden="true">
            <picture>
              <source
                media="(max-width: 768px)"
                srcset="@/assets/images/home/banner-mockup-mobile.svg"
              />
              <img src="@/assets/images/home/banner-mockup.svg" alt="" class="banner-mockup-img" />
            </picture>
          </div>
        </section>
      </section>

      <!-- Login promo bar (anonymous users only) -->
      <section v-if="!isLoggedIn" class="login-bar" aria-labelledby="login-bar-heading">
        <div class="login-bar-icon" aria-hidden="true"></div>
        <div class="login-bar-content flex align-items-center">
          <p id="login-bar-heading" class="login-bar-title">{{ $t('home.loginPromo.title') }}</p>
          <pn-button :key="locale" class="login-bar-cta" :icon="arrowRightIcon" @click="goToAuth">{{
            $t('home.login.buttonText')
          }}</pn-button>
        </div>
      </section>

      <section v-else class="orders-section" aria-labelledby="orders-heading">
        <h2 id="orders-heading" class="orders-heading">{{ $t('home.previousOrders.title') }}</h2>
        <div class="orders-grid flex">
          <article v-for="order in previousOrders" :key="order.renderKey" class="order-card">
            <div class="order-image-wrap" aria-hidden="true">
              <PdfViewer
                v-if="order.previewFileUrl"
                :src="order.previewFileUrl"
                :hide-ui="true"
                class="order-image"
              />
              <img v-else :src="order.image" alt="" class="order-image order-image--placeholder" />
            </div>
            <pn-tag
              class="order-status"
              :appearance="getOrderStatusAppearance(order.status)"
              :icon="getOrderStatusIcon(order.status)"
              :label="getOrderStatusLabel(order.status)"
            ></pn-tag>
            <h3 class="order-title">{{ order.name }}</h3>
            <p class="order-date pn-xs">{{ getOrderDateLabel(order.status, order.createdOn) }}</p>
          </article>
        </div>
        <pn-button
          :key="locale"
          class="orders-all-cta"
          appearance="light"
          variant="outlined"
          @click="goToPreviousOrders"
        >
          {{ $t('home.previousOrders.seePreviousOrders') }}
        </pn-button>
      </section>

      <!-- How to place a mail-out order -->
      <section class="steps-section" aria-labelledby="steps-heading">
        <h2 id="steps-heading" class="section-heading section-heading--center">
          {{ $t('home.steps.title') }}
        </h2>
        <div class="steps-grid flex">
          <article v-for="(step, index) in stepItems" :key="index" class="step-item">
            <div class="step-card">
              <div class="step-media-zone" aria-hidden="true">
                <img :src="step.image" alt="" class="step-photo" />
              </div>
              <div class="step-content">
                <h3 class="step-title">{{ $t(`home.steps.items.${index}.title`) }}</h3>
                <p class="step-description">{{ $t(`home.steps.items.${index}.description`) }}</p>
                <div v-if="step.onLinkClick" class="step-link-wrap">
                  <pn-text-link
                    class="text-link"
                    href="#"
                    :icon="downloadIcon"
                    @click.prevent="step.onLinkClick"
                  >
                    {{ $t(`home.steps.items.${index}.linkLabel`) }}
                  </pn-text-link>
                </div>
              </div>
            </div>
          </article>
        </div>
      </section>

      <!-- Simple and affordable -->
      <section class="pricing-section" aria-labelledby="pricing-heading">
        <h2 id="pricing-heading" class="section-heading section-heading--center">
          {{ $t('home.pricing.sectionTitle') }}
        </h2>
        <div class="pricing-grid">
          <!-- Price calculator card -->
          <article class="info-card info-card--blue" aria-labelledby="pricing-card-title">
            <div class="info-card-media">
              <img
                src="@/assets/images/home/pricing-options.svg"
                :alt="$t('home.pricing.title')"
                class="info-card-photo pricing-photo"
              />
            </div>
            <div class="info-card-content">
              <h3 id="pricing-card-title" class="info-card-title">
                {{ $t('home.pricing.title') }}
              </h3>
              <p class="info-card-description">{{ $t('home.pricing.description') }}</p>
              <div class="info-card-links">
                <pn-text-link
                  v-if="!isLoggedIn || !isBusinessUser"
                  :label="$t('home.pricing.privateLinkLabel')"
                  :href="priceListPrivateUrl"
                  :icon="openInNewIcon"
                  target="_blank"
                ></pn-text-link>
                <span v-if="!isLoggedIn" class="pricing-links-separator" aria-hidden="true">|</span>
                <pn-text-link
                  v-if="!isLoggedIn || isBusinessUser"
                  :label="$t('home.pricing.businessLinkLabel')"
                  :href="priceListBusinessUrl"
                  :icon="openInNewIcon"
                  target="_blank"
                ></pn-text-link>
              </div>
            </div>
          </article>

          <!-- Production card -->
          <article class="info-card info-card--peach" aria-labelledby="production-card-title">
            <div class="info-card-media">
              <img
                src="@/assets/images/home/direct-mail.svg"
                :alt="$t('home.production.title')"
                class="info-card-photo production-photo"
              />
            </div>
            <div class="info-card-content">
              <h3 id="production-card-title" class="info-card-title">
                {{ $t('home.production.title') }}
              </h3>
              <p class="info-card-description">{{ $t('home.production.description') }}</p>
            </div>
          </article>
        </div>
      </section>

      <!-- FAQ -->
      <section class="faq-section" aria-labelledby="faq-heading">
        <h2 id="faq-heading" class="section-heading">{{ $t('home.faq.title') }}</h2>
        <pn-accordion>
          <pn-accordion-row
            v-for="(_, index) in faqIndices"
            :key="index"
            :label="$t(`home.faq.items.${index}.question`)"
          >
            <p class="faq-answer">{{ $t(`home.faq.items.${index}.answer`) }}</p>
          </pn-accordion-row>
        </pn-accordion>
      </section>
    </div>
  </main>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, onBeforeUnmount, onMounted, ref, watch, watchEffect } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useTopbarStore } from '@/store/topbar';
import { useLoaderStore } from '@/store/loader';
import { useFileUploadStore } from '@/store/address';
import { useNotificationStore } from '@/store/notification';
import { useHomeStore } from '@/store/home';
import constants from '@/constant';
import PdfViewer from '@/components/common/PdfViewer.vue';
import {
  download as downloadIcon,
  arrow_right as arrowRightIcon,
  submit as submitIcon,
  clock as clockIcon,
  alert_info_circle as alertInfoCircleIcon,
  rotate as rotateIcon,
  truck as truckIcon,
} from 'pn-design-assets/pn-assets/icons.js';
import { arrow_left as arrowLeftIcon } from 'pn-design-assets/pn-assets/icons.js';
import { open_in_new as openInNewIcon } from 'pn-design-assets/pn-assets/icons.js';
import uploadYourDesignPng from '@/assets/images/home/upload-your-design.png';
import recipientAddressSvg from '@/assets/images/home/recipient-address.svg';
import printsDeliversSvg from '@/assets/images/home/prints-delivers.svg';
import pdfPlaceholderPng from '@/assets/images/home/pdf.png';
import pdfGuideEn from '@/assets/pdf/pdf-guide-en.pdf?url';
import pdfGuideSv from '@/assets/pdf/pdf-guide-sv.pdf?url';
import { toYMD } from '@/utils/common';
import type { MyOrderStatus } from '@/types/myOrders';
import type { MyOrder } from '@/types/myOrders';
import { fireEvent } from '@/utils/gtmTracking';

const topBarStore = useTopbarStore();
const router = useRouter();
const loaderStore = useLoaderStore();
const addressStore = useFileUploadStore();
const notificationStore = useNotificationStore();
const homeStore = useHomeStore();
const { t: $t, locale } = useI18n();
const isSideMenuExpanded = ref(false);
let sideMenuResizeObserver: ResizeObserver | null = null;
let isMyOrdersLoaded = false;
let isMyOrdersLoading = false;
let orderPreviewRequestSequence = 0;
const orderPreviewUrls = ref<Record<string, string>>({});
const orderPreviewSignature = ref('');
const { GTM_TAGS } = constants;

const isLoggedIn = computed(() => topBarStore.topBar?.isLoggedIn);
const isBusinessUser = computed(() => topBarStore.topBar?.isBusinessUser);
const isEnglishLocale = computed(() => locale.value === 'en');
const priceListPrivateUrl = computed(() =>
  isEnglishLocale.value
    ? constants.HOME.PRICE_LIST_PRIVATE_URL_EN
    : constants.HOME.PRICE_LIST_PRIVATE_URL
);
const priceListBusinessUrl = computed(() =>
  isEnglishLocale.value
    ? constants.HOME.PRICE_LIST_BUSINESS_URL_EN
    : constants.HOME.PRICE_LIST_BUSINESS_URL
);
const faqIndices = [0, 1, 2, 3, 4, 5, 6];
const pdfGuideUrl = computed(() => (locale.value === 'sv' ? pdfGuideSv : pdfGuideEn));

watchEffect(() => {
  addressStore.setColumnHeaders(
    [
      $t('excelUpload.columnHeaders.name'),
      $t('excelUpload.columnHeaders.addressRow2Optional'),
      $t('excelUpload.columnHeaders.addressRow3Optional'),
      $t('excelUpload.columnHeaders.zipCode'),
      $t('excelUpload.columnHeaders.city'),
      $t('excelUpload.columnHeaders.addressSource'),
    ],
    [
      $t('excelUpload.columnHeaders.errors'),
      $t('excelUpload.columnHeaders.name'),
      $t('excelUpload.columnHeaders.addressRow2Optional'),
      $t('excelUpload.columnHeaders.addressRow3Optional'),
      $t('excelUpload.columnHeaders.zipCode'),
      $t('excelUpload.columnHeaders.city'),
      $t('excelUpload.columnHeaders.addressSource'),
    ]
  );
});

const downloadExcelTemplate = async () => {
  loaderStore.showLoader();
  try {
    await addressStore.downloadTemplate(locale.value, String($t('excelUpload.templateFileName')));
  } catch (err: unknown) {
    const errorMessage = err instanceof Error ? err.message : 'excelUpload.errors.uploadFailed';
    notificationStore.addNotification({
      type: 'error',
      messageKey: errorMessage,
      closable: true,
      hide: false,
    });
  } finally {
    loaderStore.hideLoader();
  }
};

const downloadPdfGuide = () => {
  window.open(pdfGuideUrl.value, '_blank');
};

const stepItems = [
  { image: uploadYourDesignPng, onLinkClick: downloadPdfGuide },
  { image: recipientAddressSvg, onLinkClick: downloadExcelTemplate },
  { image: printsDeliversSvg, onLinkClick: null },
];

const revokePreviewUrls = (urls: Record<string, string>): void => {
  Object.values(urls).forEach((url) => URL.revokeObjectURL(url));
};

const buildPreviewSignature = (orders: MyOrder[]): string => {
  return orders
    .slice(0, 3)
    .map((order) => `${order.orderId}:${order.previewUrl ?? ''}`)
    .join('|');
};

const updateOrderPreviews = async (orders: MyOrder[]): Promise<void> => {
  const nextSignature = buildPreviewSignature(orders);
  if (nextSignature === orderPreviewSignature.value) {
    return;
  }

  const requestId = ++orderPreviewRequestSequence;
  orderPreviewSignature.value = nextSignature;

  const nextPreviewUrls: Record<string, string> = {};

  await Promise.all(
    orders.slice(0, 3).map(async (order) => {
      if (!order.previewUrl) {
        return;
      }

      try {
        const previewResponse = await fetch(order.previewUrl);
        if (!previewResponse.ok) {
          return;
        }

        const previewBlob = await previewResponse.blob();
        nextPreviewUrls[order.orderId] = URL.createObjectURL(previewBlob);
      } catch {
        // Signed URL may be expired; card falls back to static image.
      }
    })
  );

  if (requestId !== orderPreviewRequestSequence) {
    revokePreviewUrls(nextPreviewUrls);
    return;
  }

  revokePreviewUrls(orderPreviewUrls.value);
  orderPreviewUrls.value = nextPreviewUrls;
};

const previousOrders = computed(() => {
  return homeStore.myOrders.slice(0, 3).map((order, index) => {
    return {
      ...order,
      image: pdfPlaceholderPng,
      previewFileUrl: orderPreviewUrls.value[order.orderId] ?? '',
      renderKey: `${order.orderId}-${index}`,
    };
  });
});

const getApiStatusKey = (status: string): string => status.trim().toUpperCase();

const normalizeStatusLabel = (status: string): string => {
  return status
    .trim()
    .toLowerCase()
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const getOrderStatusIcon = (status: MyOrderStatus): string => {
  switch (getApiStatusKey(status)) {
    case constants.MY_ORDER_STATUS.PLACED:
      return submitIcon;
    case constants.MY_ORDER_STATUS.UNPROCESSED:
      return clockIcon;
    case constants.MY_ORDER_STATUS.PROCESSED:
      return alertInfoCircleIcon;
    case constants.MY_ORDER_STATUS.STARTED:
      return rotateIcon;
    case constants.MY_ORDER_STATUS.DELIVERED:
      return truckIcon;
    default:
      return submitIcon;
  }
};

const getOrderStatusLabel = (status: MyOrderStatus): string => {
  return normalizeStatusLabel(status);
};

const getOrderStatusAppearance = (status: MyOrderStatus): string => {
  switch (getApiStatusKey(status)) {
    case constants.MY_ORDER_STATUS.PLACED:
    case constants.MY_ORDER_STATUS.UNPROCESSED:
      return 'discrete';
    case constants.MY_ORDER_STATUS.DELIVERED:
      return 'positive';
    default:
      return '';
  }
};

const getOrderDateLabel = (status: MyOrderStatus, createdOn: string): string => {
  const normalizedStatus = getApiStatusKey(status);
  const datePrefix =
    normalizedStatus === constants.MY_ORDER_STATUS.STARTED
      ? String($t('home.previousOrders.datePrefixStarted'))
      : String($t('home.previousOrders.datePrefixOrdered'));
  return `${datePrefix} ${toYMD(new Date(createdOn))}`;
};

const loadMyOrders = async (): Promise<void> => {
  if (!isLoggedIn.value || isMyOrdersLoaded || isMyOrdersLoading) {
    return;
  }

  isMyOrdersLoading = true;

  try {
    await homeStore.fetchMyOrders();
    isMyOrdersLoaded = true;
  } catch {
    // Keep Home page functional even if order history fails.
  } finally {
    isMyOrdersLoading = false;
  }
};

const refreshHomeAfterTopbarReady = async (): Promise<void> => {
  loaderStore.showLoader();
  try {
    await Promise.all([homeStore.getProducts(), loadMyOrders()]);
  } finally {
    loaderStore.hideLoader();
  }
};

const goToOrder = () => {
  fireEvent(GTM_TAGS.GO_TO_ORDER);
  router.push({ name: 'order' });
};

const goToAuth = () => {
  topBarStore.topBar?.login();
};

const goToPreviousOrders = () => {
  router.push({ name: 'myOrders' });
};

const updateSideMenuState = () => {
  const sideMenuElement = document.querySelector('pn-side-menu') as HTMLElement | null;
  if (!sideMenuElement) {
    isSideMenuExpanded.value = false;
    return;
  }
  isSideMenuExpanded.value = sideMenuElement.getBoundingClientRect().width > 120;
};

onBeforeMount(async () => {
  loaderStore.showLoader();
  try {
    await Promise.all([homeStore.fetchServiceCard(), homeStore.getProducts(), loadMyOrders()]);
  } finally {
    loaderStore.hideLoader();
  }
});

onMounted(() => {
  updateSideMenuState();
  const sideMenuElement = document.querySelector('pn-side-menu') as HTMLElement | null;
  if (sideMenuElement && typeof ResizeObserver !== 'undefined') {
    sideMenuResizeObserver = new ResizeObserver(() => {
      updateSideMenuState();
    });
    sideMenuResizeObserver.observe(sideMenuElement);
  }
  window.addEventListener('resize', updateSideMenuState);
});

onBeforeUnmount(() => {
  orderPreviewRequestSequence += 1;
  revokePreviewUrls(orderPreviewUrls.value);

  if (sideMenuResizeObserver) {
    sideMenuResizeObserver.disconnect();
  }
  window.removeEventListener('resize', updateSideMenuState);
});

watch(
  () => homeStore.myOrders,
  (orders) => {
    void updateOrderPreviews(orders);
  },
  { immediate: true }
);

watch(
  () => topBarStore.topBar,
  async (topBar) => {
    if (topBar !== undefined) {
      await refreshHomeAfterTopbarReady();
    }
  }
);
</script>

<style scoped lang="scss">
/* Page container */
.home-page {
  background: var(--color-primary-contrast);
}

.page-wrapper {
  position: relative;
  max-width: var(--home-page-max-width);
  margin: 0 auto;
  padding: var(--home-page-padding-desktop);
  display: flex;
  flex-direction: column;
  gap: var(--home-page-gap-lg);
}

.home-page--side-menu-expanded .page-wrapper {
  padding-left: 80px;
  padding-right: 80px;
}

.hero-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-top: var(--home-banner-top-padding);
}

.home-top-nav {
  display: flex;
  align-items: center;
  min-height: 40px;
  width: min(100%, var(--home-banner-frame-width));
  margin: 0 auto;
}

/* Banner */
.banner-card {
  background: var(--home-banner-bg);
  border-radius: 48px;
  width: min(100%, var(--home-banner-frame-width));
  margin: 0 auto;
  padding: clamp(20px, 2.5vw, 32px);
  display: flex;
  align-items: center;
  justify-content: space-between;
  // gap: clamp(16px, 2.5vw, 32px);
  overflow: hidden;
}

.banner-content {
  display: flex;
  flex-direction: column;
  gap: clamp(20px, 2.5vw, 32px);
  flex: 1 1 360px;
  color: var(--color-primary-contrast);
  min-width: 0;
  max-width: clamp(320px, 45%, 520px);
  z-index: 2;
}

.banner-text {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.banner-title {
  font-weight: 700;
  margin: 0;
  color: var(--color-primary-contrast);
  white-space: normal;
}

.banner-description {
  font-weight: 500;
  margin: 0;
  color: var(--color-primary-contrast);
  max-width: 100%;
}

.banner-cta {
  width: fit-content;
}

.banner-mockup {
  flex: 1 1 420px;
  min-width: 280px;
  width: min(100%, 636px);
  max-width: 636px;
  height: auto;
  aspect-ratio: 636 / 367;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.banner-mockup picture {
  display: block;
  width: 100%;
  height: 100%;
}

.banner-mockup-img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* Login bar */
.login-bar {
  display: flex;
  align-items: flex-end;
  gap: 24px;
}

.login-bar-content {
  background: var(--home-login-bg);
  border-radius: var(--home-login-radius);
  padding: var(--home-login-padding);
  gap: var(--home-login-gap);
  justify-content: center;
  flex: 1;
}

.login-bar-icon {
  width: var(--home-login-icon-size);
  height: var(--home-login-icon-size);
  border-radius: 999px;
  background: var(--home-login-icon-bg);
  flex-shrink: 0;
}

.login-bar-title {
  font-size: var(--home-login-title-size);
  font-weight: 700;
  line-height: var(--home-login-title-line-height);
  color: var(--home-login-title-color);
  margin: 0;
  white-space: nowrap;
  flex-shrink: 0;
}

.login-bar-subtitle {
  font-size: var(--home-login-subtitle-size);
  font-weight: 500;
  line-height: var(--home-login-subtitle-line-height);
  color: var(--color-primary-contrast);
  margin: 0;
  flex: 1;
}

.login-bar-cta {
  flex-shrink: 0;
}

.orders-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;
}

.orders-heading {
  margin: 0;
  font-size: var(--home-section-title-size);
  font-weight: 700;
  line-height: var(--home-section-title-line-height);
  color: var(--color-text-primary);
  text-align: center;
}

.orders-grid {
  width: 100%;
  gap: var(--home-grid-gap);
  align-items: stretch;
}

.order-card {
  flex: 1;
  min-width: 0;
  background: var(--color-primary-contrast);
  border-radius: 8px;
  box-shadow: var(--home-elevation-sm);
  padding: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.order-image-wrap {
  width: 210px;
  height: 140px;
  overflow: hidden;
  border: 1px solid;
}

.order-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.order-image-wrap :deep(iframe.order-image) {
  display: block;
  width: calc(100% + 18px);
  height: calc(100% + 18px);
  margin-right: -18px;
  margin-bottom: -18px;
}

.order-image--placeholder {
  object-fit: contain;
  padding: 12px;
}

.order-status {
  margin-top: 24px;
}

.order-title {
  margin: 8px 0 0;
  font-size: var(--home-login-title-size);
  font-weight: 700;
  line-height: var(--home-login-title-line-height);
  color: var(--color-text-primary);
}

.order-date {
  margin: 8px 0 0;
  color: var(--color-heading);
}

.order-action {
  margin-top: 8px;
}

.orders-all-cta {
  width: fit-content;
}

/* Section headings */
.section-heading {
  font-size: var(--home-section-title-size);
  font-weight: 700;
  line-height: var(--home-section-title-line-height);
  color: var(--color-text-primary);
  margin: 0;
}

.section-heading--center {
  text-align: center;
}

/* Steps section */
.steps-section {
  display: flex;
  flex-direction: column;
  gap: var(--home-section-gap-xl);
}

.steps-grid {
  gap: var(--home-grid-gap);
  align-items: stretch;
}

.step-item {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.step-card {
  background: var(--color-primary-contrast);
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.step-media-zone {
  height: var(--home-step-media-height);
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.step-photo {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.step-content {
  padding: 24px 0;
  display: flex;
  flex-direction: column;
  gap: var(--home-step-content-gap);
  flex: 1;
}

.step-title {
  font-size: var(--home-step-title-size);
  font-weight: 700;
  line-height: var(--home-step-title-line-height);
  color: var(--color-text-primary);
  margin: 0;
}

.step-description {
  font-size: var(--home-body-text-size);
  line-height: var(--home-body-text-line-height);
  color: var(--color-content-secondary);
  margin: 0;
}

.step-link-wrap {
  margin-top: auto;
  padding-top: 24px;
}

/* Pricing section */
.pricing-section {
  display: flex;
  flex-direction: column;
  gap: var(--home-section-gap-xl);
}

.pricing-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--home-grid-gap);
  align-items: stretch;
}

/* Custom info cards (pricing + production) */
.info-card {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: var(--home-elevation-sm);
  display: flex;
  flex-direction: column;
}

.info-card-media {
  height: var(--home-card-media-height);
  overflow: hidden;
  flex-shrink: 0;
}

.info-card-photo {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.pricing-photo {
  object-position: center top;
}

.production-photo {
  object-position: center 35%;
}

.info-card-content {
  flex: 1;
  padding: 24px;
  margin-top: -20px;
  border-radius: 20px 20px 0 0;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.info-card--blue .info-card-content {
  background: var(--color-focus);
}

.info-card--peach .info-card-content {
  background: var(--color-brand-secondary-lightest);
}

.info-card-title {
  font-size: var(--home-step-title-size);
  font-weight: 700;
  line-height: var(--home-step-title-line-height);
  color: var(--color-text-primary);
  margin: 0;
}

.info-card-description {
  font-size: var(--home-body-text-size);
  line-height: var(--home-body-text-line-height);
  color: var(--color-content-secondary);
  margin: 0;
  flex: 1;
}

.info-card-links {
  display: flex;
  align-items: center;
  gap: var(--home-card-link-gap);
  flex-wrap: nowrap;
  margin-top: auto;
  padding-top: 12px;
}

.pricing-links-separator {
  color: var(--color-primary);
  font-size: var(--home-body-text-size);
  line-height: var(--home-body-text-line-height);
}

/* FAQ */
.faq-section {
  display: flex;
  flex-direction: column;
  gap: var(--home-faq-gap);
}

.faq-answer {
  font-size: var(--home-body-text-size);
  line-height: var(--home-body-text-line-height);
  color: var(--color-text-primary);
  margin: 0;
  max-width: var(--home-faq-max-width);
}

/* Text link */
.text-link {
  display: inline-flex;
  align-items: center;
  gap: var(--home-text-link-gap);
  color: var(--color-primary);
  text-decoration: none;
  font-weight: 500;
  cursor: pointer;
}

.text-link:hover {
  text-decoration: underline;
}

.text-link:focus-visible {
  outline: 2px solid var(--color-focus);
  outline-offset: 2px;
  border-radius: var(--home-focus-border-radius);
  text-decoration: none;
}

/* =========================
	 Responsive
	 ========================= */
@media (max-width: 1024px) {
  .home-page--side-menu-expanded .page-wrapper {
    padding: var(--home-page-padding-mobile);
  }

  .page-wrapper {
    padding: var(--home-page-padding-tablet);
    gap: var(--home-page-gap-tablet);
  }

  .banner-card {
    padding: 24px;
  }

  .banner-content {
    max-width: 360px;
  }

  .banner-mockup {
    width: 380px;
    height: 210px;
    flex-shrink: 0;
  }

  .login-bar {
    flex-wrap: wrap;
    align-items: center;
    gap: 16px;
  }

  .login-bar-content {
    border-radius: var(--home-login-radius-tablet);
    width: 100%;
  }

  .steps-grid,
  .orders-grid {
    flex-direction: column;
  }

  .pricing-grid {
    grid-template-columns: 1fr;
  }

  .order-card {
    width: 100%;
  }

  .info-card-links {
    flex-wrap: wrap;
  }
}

@media (max-width: 768px) {
  .home-page--side-menu-expanded .page-wrapper {
    padding: var(--home-page-padding-mobile);
  }

  .hero-section {
    margin: 0 -16px;
    padding-top: 0;
  }

  .banner-card {
    flex-direction: column;
    align-items: flex-start;
    border-radius: 0;
    padding: 48px 0 24px;
  }

  .banner-content {
    max-width: none;
    width: 100%;
    padding: 0 32px;
    gap: 24px;
  }

  .banner-title {
    font-size: 40px;
    line-height: 40px;
    max-width: 260px;
    white-space: normal;
  }

  .banner-mockup {
    width: 100%;
    max-width: 100%;
    min-width: 0;
    height: auto;
    aspect-ratio: 1340 / 540;
    flex-shrink: 0;
  }

  .banner-mockup picture {
    height: auto;
  }

  .banner-mockup-img {
    height: auto;
    object-fit: contain;
    object-position: center;
    transform: none;
  }
}

@media (max-width: 640px) {
  .home-page--side-menu-expanded .page-wrapper {
    padding: var(--home-page-padding-mobile);
  }

  .page-wrapper {
    padding: var(--home-page-padding-mobile);
    gap: var(--home-page-gap-mobile);
  }

  .banner-card {
    min-height: auto;
  }

  .banner-content {
    width: 100%;
    flex: auto;
  }

  .banner-title {
    font-size: 40px;
    line-height: 40px;
    max-width: 260px;
  }

  .banner-mockup {
    flex-shrink: 0;
  }

  .banner-mockup-img {
    transform: none;
  }

  .login-bar {
    margin: 0 -16px;
    padding-right: 16px;
    overflow: hidden;
    align-items: center;
    gap: 12px;
  }

  .login-bar-icon {
    margin-left: -44px;
    flex-shrink: 0;
  }

  .login-bar-content {
    border-radius: var(--home-login-radius-mobile);
    padding: var(--home-login-padding-mobile);
    flex: 1;
    min-width: 0;
    gap: 16px;
    align-items: center;
  }

  .login-bar-title {
    white-space: normal;
    flex: 1;
  }

  .orders-section {
    gap: 24px;
  }

  .order-card {
    padding: 20px;
  }

  .order-image-wrap {
    width: 100%;
    height: auto;
    aspect-ratio: 3 / 2;
  }
}
</style>
